import {
    VENDOR_DEFINED,
    OPERATOR_PLATFORM,
    TPM,
    SBC,
    SRT,
    OPERATOR_CONNECT,
    EIGHTBYEIGHT,
    EIGHTBYEIGHT_STATUS_DISPLAY_DETAIL_TO_STATUS,
    eightByEightData,
    drStatusesColour,
    MICROSOFT_GRAPH,
    APP_AUTH,
    APP_AUTH_GCCH,
} from '../../constants';
import { PlatformEnums } from 'utils/platforms';
import CardDetails from '../CardDetails';
import { getStatusConfig } from '../VendorCard/Vendor/helper';
import getSipTrunkCardStatus from './sipTrunkStatus';

/**
 * Derive an 8x8 card status from the list-level statusDisplay when the
 * per-service status hasn't loaded (e.g. old services whose GET /Service/{id}
 * 500s). Returns null when there's no statusDisplay to derive from.
 * @param {Object} statusDisplay - service.statusDisplay from the list endpoint
 * @returns {Object|null} card status or null
 */
export const getEightByEightFallbackStatus = (statusDisplay) => {
    if (!statusDisplay) return null;
    const mapped =
        EIGHTBYEIGHT_STATUS_DISPLAY_DETAIL_TO_STATUS[
            statusDisplay.statusDisplayDetail
        ] ?? 2; // default: Pending Access
    const entry = eightByEightData.statusId[mapped];
    const label = entry?.statusLabels?.[0];
    return label ? { statusLabel: label, ...entry[label] } : null;
};

/**
 * Get the card status for a given service
 * @param {Object} service - The service object
 * @param {Map} vendorStatusResults - Map of vendor/operator platform service statuses
 * @param {Object} serviceStatus - Status object from useServicesDashboard
 * @returns {Object} Card status with color, statusLabel, animation, etc.
 */
export const GetCardStatus = (service, serviceStatus) => {
    if (!service) return null;
    const vendorStatus = getStatusConfig(service?.statusDisplay);

    const statusFromDisplay = (() => {
        const level = service?.statusDisplay?.statusDisplayLevel;
        if (level === undefined || level === null) return null;

        const cfg =
            drStatusesColour?.[String(level)] || drStatusesColour?.[level];
        if (!cfg?.color) return null;

        const statusLabel =
            service?.statusDisplay?.statusDisplayDetail ||
            service?.statusDisplay?.statusDisplayText ||
            service?.statusDisplay?.statusDisplayExtended ||
            service?.status;

        return {
            ...CardDetails(service.serviceType),
            color: cfg.color,
            isAnimated: !!cfg.animated,
            animation: !!cfg.animated,
            hasStripes: !!cfg.animated,
            statusLabel,
            statusDisplay: service?.statusDisplay,
        };
    })();

    switch (service.serviceType) {
        case VENDOR_DEFINED:
        case OPERATOR_PLATFORM: {
            return vendorStatus;
        }
        case TPM:
            return { ...serviceStatus[OPERATOR_CONNECT] };
        case MICROSOFT_GRAPH: {
            const statusKey =
                service?.platformID === PlatformEnums.GCC
                    ? APP_AUTH_GCCH
                    : APP_AUTH;
            return { ...serviceStatus[statusKey] };
        }
        case EIGHTBYEIGHT: {
            const eightStatus = serviceStatus[EIGHTBYEIGHT];
            // Trust the reducer status only when it has a statusLabel and a
            // matching serviceId; otherwise derive from the fresh statusDisplay.
            if (
                eightStatus?.statusLabel &&
                eightStatus?.serviceId === service?.id
            ) {
                return { ...eightStatus };
            }
            return (
                getEightByEightFallbackStatus(service?.statusDisplay) ?? {
                    ...eightStatus,
                }
            );
        }
        // Both Cloud SIP services report their status on the trunk status
        // scale (0 OK … 20 Error), as their cards read it.
        case SBC:
        case SRT:
            return {
                ...CardDetails(service.serviceType),
                ...getSipTrunkCardStatus(
                    service?.statusDisplay?.statusDisplayLevel,
                ),
            };
        default: {
            // Prefer consolidated statusDisplay until the context status is hydrated
            // (the context starts as gray defaults which would expand everything).
            const contextStatus = serviceStatus?.[service.serviceType];
            const hasHydratedContext =
                !!contextStatus &&
                ((contextStatus?.color && contextStatus.color !== 'gray') ||
                    !!contextStatus?.statusLabel ||
                    !!contextStatus?.statusDisplay);

            return (
                (hasHydratedContext
                    ? { ...CardDetails(service.serviceType), ...contextStatus }
                    : statusFromDisplay) ||
                contextStatus || {
                    ...CardDetails(service.serviceType),
                    color: 'gray',
                    animation: false,
                }
            );
        }
    }
};
